import type { Navigator } from "../app/screen";
import { mountBackdrop, type Backdrop } from "../fx/backdrop";

/**
 * THE PAGE SHELL. Every lobby screen that is not the menu is built from this.
 *
 * ⭐ **IT EXISTS SO "A WAY OUT" IS STRUCTURAL RATHER THAN REMEMBERED.** In the old client, five
 * lobby screens off the main menu drew no exit at all - PLAY, DECKS, PROFILE, SETTINGS and the
 * standings - and `walk_surfaces` scored them green because every oracle we had measured
 * appearance. Here a screen cannot be built without asking for the shell, and the shell brings BACK
 * with it. The router's affordance check is then a second line of defence, not the only one.
 *
 * ⭐ **AND IT IS WHERE THE APPROVED LANGUAGE LIVES ONCE.** Title, rule, panel and back plate are
 * declared here so a new screen inherits the type ladder and the palette rather than choosing them.
 * `revival-design-system`: fix the SYSTEM, not the page.
 */

export interface Shell {
  /** The root element to append to the host. */
  el: HTMLElement;
  /** Where a screen puts its own content. */
  body: HTMLElement;
}

/**
 * The lobby backdrop, mounted once per page and released when the page goes.
 *
 * ⭐ **THE LOBBY PAGES WERE FLAT BLACK.** Alex, 2026-08-14: *"all the pages are basic, just black.
 * Would they look more high end with a background? Something like our home page which is beautiful
 * and has the dynamic motion?"* Yes - and the answer is the SAME backdrop the menu uses, not a
 * second one. `mountBackdrop` is the one owner of that painting and its wind.
 *
 * ⚠️ **BEHIND A SCRIM, AND DELIBERATELY QUIETER THAN THE MENU'S.** The menu's job is to be looked
 * at; a lobby page's job is to be read. The same image at the same strength behind a deck list
 * would be showing off at the player's expense.
 *
 * ⚠️ **RELEASED ON THE NEXT PAGE.** It runs a Pixi ticker; one per visited screen is a leak whose
 * symptom is the lobby getting slower the longer you use it. `releasePageChrome()` is called by the
 * Router before it mounts anything else.
 */
let pageBackdrop: Backdrop | null = null;

export function releasePageChrome(): void {
  pageBackdrop?.destroy();
  pageBackdrop = null;
}

export function pageShell(nav: Navigator, title: string, sub?: string): Shell {
  const el = document.createElement("main");
  el.className = "page";

  // The living background, then a scrim, then the page's own content on top.
  const bg = document.createElement("div");
  bg.className = "page__bg";
  bg.setAttribute("aria-hidden", "true");
  el.append(bg);
  releasePageChrome();
  void mountBackdrop(bg).then((b) => { pageBackdrop = b; });

  const head = document.createElement("header");
  head.className = "page__head";

  const h = document.createElement("h1");
  h.className = "page__title";
  h.textContent = title;
  head.append(h);

  if (sub) {
    const s = document.createElement("p");
    s.className = "page__sub";
    s.textContent = sub;
    head.append(s);
  }

  // The palette's own device where a heading meets its content - the same rule every screen uses,
  // never a private hairline.
  const rule = document.createElement("div");
  rule.className = "page__rule";
  rule.setAttribute("aria-hidden", "true");
  head.append(rule);

  const body = document.createElement("div");
  body.className = "page__body";

  /* 🔴 **A SCROLLER THAT ENDS FLUSH WITH ITS EDGE LOOKS LIKE THE END OF THE CONTENT.**
   * Alex, 2026-08-20, on the campaign hub in landscape: the subtitle says "THREE WAYS TO PLAY
   * ALONE" and two are visible. The third was reachable by scrolling and nothing said so - which
   * is `revival-ui-review` §0's defect class exactly: the answer exists and is invisible.
   *
   * ⭐ ONE OWNER, HERE. Every `.page` screen gets this for free rather than each one inventing a
   * hint, and `data-more` is set from the SCROLL POSITION, so the fade disappears at the bottom
   * instead of implying content that is not there.
   *
   * ⚠️ NOT `animation-timeline: scroll()`. It is the tidy CSS-only answer and iOS Safari does not
   * support it - and iPhone landscape is precisely the surface this was written for. */
  const markMore = () => {
    const more = body.scrollHeight - body.clientHeight - body.scrollTop > 2;
    body.dataset["more"] = more ? "1" : "0";
  };
  let queued = 0;
  const remark = () => {
    if (queued) return;
    queued = requestAnimationFrame(() => { queued = 0; markMore(); });
  };
  body.addEventListener("scroll", remark, { passive: true });
  window.addEventListener("resize", remark);
  // Size alone is not enough: a screen renders INTO this box, so the content changes without the
  // box changing. Watch both.
  new ResizeObserver(remark).observe(body);
  new MutationObserver(remark).observe(body, { childList: true, subtree: true });
  remark();

  // ⛔ BACK IS NOT OPTIONAL AND IT IS NOT THE SCREEN'S DECISION. Quiet and low - the way out of a
  // screen is never the thing it wants pressed.
  const back = document.createElement("button");
  back.type = "button";
  back.className = "plate plate--quiet page__back";
  back.dataset["label"] = "BACK";
  const backFace = document.createElement("span");
  backFace.className = "plate__face";
  backFace.setAttribute("aria-hidden", "true");
  const backLabel = document.createElement("span");
  backLabel.className = "plate__label";
  backLabel.textContent = "BACK";
  back.append(backFace, backLabel);
  back.addEventListener("click", () => nav.back());

  el.append(head, body, back);
  return { el, body };
}

/**
 * A row of choices, one plate each.
 *
 * ⚠️ **A PLATE WITH NOTHING BEHIND IT IS WORSE THAN NO PLATE.** A disabled choice is rendered
 * disabled and says WHY, rather than being drawn live and doing nothing when pressed - on a screen
 * whose only job is "pick where you play", a dead control reads as the game being broken.
 */
export interface Choice {
  label: string;
  sub?: string;
  disabled?: string; // the reason, shown to the player
  onPick?: () => void;
}

export function choiceList(choices: Choice[]): HTMLElement {
  const list = document.createElement("div");
  list.className = "choices";

  for (const c of choices) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "plate";
    b.dataset["label"] = c.label;

    const face = document.createElement("span");
    face.className = "plate__face";
    face.setAttribute("aria-hidden", "true");

    const l = document.createElement("span");
    l.className = "plate__label";
    l.textContent = c.label;

    const s = document.createElement("span");
    s.className = "plate__sub";
    s.textContent = c.disabled ?? c.sub ?? "";

    b.append(face, l, s);

    if (c.disabled) {
      b.disabled = true;
      b.setAttribute("aria-disabled", "true");
      b.classList.add("plate--locked");
    } else if (c.onPick) {
      b.addEventListener("click", c.onPick);
    }

    list.append(b);
  }
  return list;
}

/** A short status line. Used for loading and for the server's own refusals. */
export function note(text: string, isError = false): HTMLElement {
  const p = document.createElement("p");
  p.className = isError ? "page__note page__note--error" : "page__note";
  // ⚠️ textContent, never innerHTML. This can carry a server message, and a server message can
  // carry another player's text.
  p.textContent = text;
  return p;
}
